import { queryOptions, QueryClient } from '@tanstack/react-query';
import { scadaKeys } from './queryKeys';
import { ScadaConfigurationPathConstants } from '../../../assetConfiguration/assetConstants/pathConstants';
import axiosInstance from '../../../../../constants/axiosInstance';

export type LiveLayoutPayload = Record<
    string,
    Array<{ purpose: string; value: unknown; id?: string }>
>;

export const graphLayoutQueryOptions = (assetId: string) =>
    queryOptions({
        queryKey: scadaKeys.graph(assetId),
        queryFn: async () => {
            const { data } = await axiosInstance().post(
                ScadaConfigurationPathConstants.GET_ASSET_GRAPHS,
                { id: assetId }
            );
            const results = data?.results;
            return {
                graphLayout: results?.layout,
                assetId: results?.nodeId || assetId,
            };
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!assetId,
    });

export const entityDetailsQueryOptions = (entityId: string) =>
    queryOptions({
        queryKey: scadaKeys.entity(entityId),
        queryFn: async () => {
            const { data } = await axiosInstance().post(
                ScadaConfigurationPathConstants.GET_ENTITY_DETAILS,
                { id: entityId }
            );
            return data?.result;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!entityId,
    });

export const liveLayoutQueryOptions = (params: { id: string; plantId: string }) =>
    queryOptions({
        queryKey: scadaKeys.live(params.id, params.plantId),
        queryFn: async ({ signal }) => {
            const { data } = await axiosInstance().get(
                ScadaConfigurationPathConstants.REAL_TIME_SCADA,
                {
                    params: { id: params.id, plantId: params.plantId },
                    signal,
                }
            );
            return (data?.data ?? data?.results ?? data) as LiveLayoutPayload;
        },
        staleTime: Infinity,
        gcTime: 5 * 60 * 1000,
        enabled: !!params.id && !!params.plantId,
    });

export const historicalDataQueryOptions = (params: {
    id: string;
    plantId: string;
    timestamp: number;
}) =>
    queryOptions({
        queryKey: scadaKeys.historicalData(params.id, params.plantId, params.timestamp),
        queryFn: async ({ signal }) => {
            const { data } = await axiosInstance().get(
                ScadaConfigurationPathConstants.REAL_TIME_SCADA,
                {
                    params: {
                        id: params.id,
                        plantId: params.plantId,
                        timeStamp: params.timestamp,
                    },
                    signal,
                }
            );
            return (data?.data ?? data?.results ?? data) as LiveLayoutPayload;
        },
        enabled: !!params.id && !!params.plantId && !!params.timestamp,
        staleTime: Infinity,
        gcTime: 30 * 60 * 1000,
    });

export const prefetchGraphDetails = (queryClient: QueryClient, assetId: string) => {
    return queryClient.prefetchQuery(graphLayoutQueryOptions(assetId));
};
